import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';

function Dashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/dashboard');
      if (!response) return;

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || data.error || 'خطأ في جلب بيانات لوحة التحكم');
        return;
      }

      setDashboard(data.dashboard);
    } catch (err) {
      setError('خطأ في جلب البيانات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return parseFloat(value || 0).toLocaleString('ar-LY');
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('ar-LY');
  };

  const getTransactionLabel = (type) => {
    const labels = {
      recharge: '🔄 شحن',
      collection: '💵 قبض',
      transfer: '↔️ تحويل',
      expense: '💸 مصروف',
      company_settlement: '🏢 تسديد شركة'
    };

    return labels[type] || type || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100" dir="rtl">
        <div className="text-xl text-gray-600">جاري التحميل...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100" dir="rtl">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-xl text-red-600 mb-4">⚠️ {error}</div>
          <button
            onClick={fetchDashboard}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const dailyStats = dashboard?.daily_stats || {};
  const recentTransactions = dashboard?.recent_transactions || [];
  const topStores = dashboard?.top_stores || [];

  return (
    <div className="min-h-screen bg-gray-100 p-8" dir="rtl">
      {/* Back Button */}
      <div className="mb-8">
        <a href="/" className="text-blue-600 hover:text-blue-800 font-bold">
          ← رجوع للرئيسية
        </a>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">
          🏢 لوحة تحكم نظام الإشارة الإداري
        </h1>
        <p className="text-gray-600 mt-2">
          نظام ERP مالي وإداري لإدارة المحلات والمحافظ والعمليات اليومية
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">إجمالي المحافظ</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {summary.total_wallets || 0}
              </p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">رصيد المحافظ</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {formatNumber(summary.total_wallet_balance)}
              </p>
            </div>
            <div className="text-4xl">💵</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">إجمالي الديون</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {formatNumber(summary.total_debts)}
              </p>
            </div>
            <div className="text-4xl">📊</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-sm">المحلات النشطة</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                {summary.active_stores || 0}
              </p>
            </div>
            <div className="text-4xl">🏪</div>
          </div>
        </div>
      </div>

      {/* Daily Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6 border-l-4 border-green-500">
          <p className="text-gray-600 text-sm">التحصيل اليومي</p>
          <p className="text-3xl font-bold text-green-700 mt-2">
            {formatNumber(dailyStats.collection)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6 border-l-4 border-blue-500">
          <p className="text-gray-600 text-sm">الشحن اليومي</p>
          <p className="text-3xl font-bold text-blue-700 mt-2">
            {formatNumber(dailyStats.recharge)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg shadow p-6 border-l-4 border-red-500">
          <p className="text-gray-600 text-sm">المصاريف اليومية</p>
          <p className="text-3xl font-bold text-red-700 mt-2">
            {formatNumber(dailyStats.expenses)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-6 border-l-4 border-purple-500">
          <p className="text-gray-600 text-sm">الصافي اليومي</p>
          <p className="text-3xl font-bold text-purple-700 mt-2">
            {formatNumber(dailyStats.net)}
          </p>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          📝 آخر العمليات المالية
        </h2>

        {recentTransactions.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            لا توجد عمليات مالية حالياً
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-right text-gray-600">النوع</th>
                  <th className="px-4 py-3 text-right text-gray-600">المبلغ</th>
                  <th className="px-4 py-3 text-right text-gray-600">الوصف</th>
                  <th className="px-4 py-3 text-right text-gray-600">التاريخ</th>
                </tr>
              </thead>

              <tbody>
                {recentTransactions.map((tx) => (
                  <tr key={tx.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded text-sm">
                        {getTransactionLabel(tx.type)}
                      </span>
                    </td>

                    <td className="px-4 py-3 font-bold text-green-600">
                      {formatNumber(tx.amount)}
                    </td>

                    <td className="px-4 py-3 text-gray-600">
                      {tx.description || '-'}
                    </td>

                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {formatDate(tx.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top Stores */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          🏪 أكثر المحلات حركة اليوم
        </h2>

        {topStores.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            لا توجد حركة للمحلات اليوم
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-right text-gray-600">المحل</th>
                  <th className="px-4 py-3 text-right text-gray-600">عدد العمليات</th>
                  <th className="px-4 py-3 text-right text-gray-600">إجمالي المبالغ</th>
                </tr>
              </thead>

              <tbody>
                {topStores.map((store) => (
                  <tr key={store.id} className="border-b hover:bg-gray-50">
                    <td className="px-4 py-3 font-bold text-gray-800">
                      {store.name}
                    </td>

                    <td className="px-4 py-3 text-gray-600">
                      {store.transaction_count || 0}
                    </td>

                    <td className="px-4 py-3 font-bold text-green-600">
                      {formatNumber(store.total_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-center">
        <button
          onClick={fetchDashboard}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition"
        >
          🔄 تحديث البيانات
        </button>
      </div>
    </div>
  );
}

export default Dashboard;